/**
 * FamilyMemberList Component
 *
 * Displays a list of family member profile cards with add button.
 */

import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont, getHeadingFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { FamilyMember } from '@/types/family-member';
import { FamilyMemberProfileCard } from './FamilyMemberProfileCard';
import { MeProfileCard } from './MeProfileCard';

type FamilyMemberListProps = {
  members: FamilyMember[];
  getCalendarCount: (memberId: string) => number;
  onMemberPress: (member: FamilyMember) => void;
  onMePress: () => void;
  onAddPress: () => void;
  onBack?: () => void;
};

export function FamilyMemberList({
  members,
  getCalendarCount,
  onMemberPress,
  onMePress,
  onAddPress,
  onBack,
}: FamilyMemberListProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.titleRow}>
          {onBack && (
            <TouchableOpacity
              onPress={onBack}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              activeOpacity={0.7}
            >
              <Ionicons name="arrow-back" size={22} color={palette.text} />
            </TouchableOpacity>
          )}
          <Text
            style={[styles.sectionTitle, { color: palette.text, fontFamily: getHeadingFont() }]}
          >
            Family Members
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.addButton, { backgroundColor: palette.primary }]}
          onPress={onAddPress}
          activeOpacity={0.7}
        >
          <Ionicons name="add" size={20} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      {members.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="people-outline" size={48} color={palette.textMuted} />
          <Text
            style={[styles.emptyText, { color: palette.textMuted, fontFamily: getBodyFont() }]}
          >
            No family members yet
          </Text>
        </View>
      ) : (
        <View style={styles.list}>
          {members.map((member) =>
            member.isMe ? (
              <MeProfileCard
                key={member.id}
                member={member}
                calendarCount={getCalendarCount(member.id)}
                onPress={onMePress}
              />
            ) : (
              <FamilyMemberProfileCard
                key={member.id}
                member={member}
                calendarCount={getCalendarCount(member.id)}
                onPress={onMemberPress}
              />
            )
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: Spacing.xxl,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Spacing.lg,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: 16, // circular
    alignItems: 'center',
    justifyContent: 'center',
  },
  list: {
    gap: Spacing.none,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Spacing.xxxl,
  },
  emptyText: {
    fontSize: 14,
    marginTop: Spacing.md,
  },
});
